<template>
  <div class="event-modal-catalog-footer">
    <Popup
      class="popup-modal__agreed"
      v-if="isSubmitAgree"
      @confirm="submittionAgreed"
      @close="isSubmitAgree = false"
      agree
    >
      {{ $t("event.catalog.sendAgree") }} <br />
      <a
        :href="
          `/static/docs/general_terms_of_publication_in_the_official_catalogue_guide_${locale}.pdf`
        "
        target="_blank"
      >
        {{ $t("event.catalog.termsPart12") }}
      </a>

      {{ $t("event.catalog.termsPart2") }}
    </Popup>

    <Popup message v-if="isSubmitSuccess" @close="isSubmitSuccess = false">
      {{ $t("event.catalog.modal.footer.submitSuccessMessage") }}
    </Popup>
    <div class="event-modal-catalog-footer__container">
      <div
        v-if="errorPriceServices"
        class="event-modal-catalog-footer__error-price"
      >
        <icon name="warning" class="item__warning-icon" />
        <span
          class="event-modal-catalog-footer__error-price-text"
          v-html="$t('event.catalog.modal.errorPriceServices')"
        ></span>
      </div>

      <div
        class="event-modal-catalog-footer__info"
        v-if="!!$slots['paid-services']"
      >
        <slot name="paid-services" />
      </div>

      <div class="event-modal-catalog-footer__summary">
        <div class="event-modal-catalog-footer__summary-price">
          <div class="event-modal-catalog-footer__summary-price-line">
            <div>{{ $t("event.catalog.modal.footer.total") }}</div>
            <span class="event-modal-catalog-footer__summary-price-wrapper">
              {{ errorPriceServices ? "N/A" : totalOrderSum }}
              <span v-html="currencySymbol"></span>
              <EventCurrencyDropdown
                v-if="status === 'draft' && priceTypes && priceTypes.length > 1"
                :value="currentCurrencyType"
                :options="priceTypes"
                @input="setCurrentCurrencyTypeHandler"
              />
            </span>
          </div>

          <div class="event-modal-catalog-footer__summary-price-line">
            <div>{{ $t("event.catalog.modal.footer.totalVAT") }}</div>
            <div>
              <span
                >{{ errorPriceServices ? "N/A" : totalOrderSumWithVAT }}
                <span v-html="currencySymbol"></span
              ></span>
            </div>
          </div>
        </div>

        <template v-if="status === 'draft'">
          <LinkIcon
            icon="back-grey"
            class="event-modal-catalog-footer__summary-back"
            :grey="true"
            :bold="true"
            v-if="isPreviewStep"
            @click="prev"
          >
            {{ $t("event.catalog.modal.footer.backBtn") }}
          </LinkIcon>

          <PrimaryButton :disabled="!isNextStepAllowed" @click="next">
            {{ nextLabel }}
          </PrimaryButton>
        </template>

        <template v-else>
          <PrimaryButton @click="closeModal()">
            {{ $t("event.catalog.modal.footer.closeBtn") }}
          </PrimaryButton>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from "vuex";
import apiCatalog from "@/api/catalog";
import steps from "@/helpers/constants/steps";
import EventCurrencyDropdown from "@/components/Custom/EventCurrencyDropdown.vue";
import { PrimaryButton, LinkIcon, Popup } from "@v2/shared/ui/components";
import { i18n } from "@v2/shared/config";

export default {
  name: "EventModalCatalogFooter",

  components: {
    Popup,
    PrimaryButton,
    LinkIcon,
    EventCurrencyDropdown
  },

  data: () => ({
    isSubmitAgree: false,
    isSubmitSuccess: false
  }),

  computed: {
    ...mapState("event", ["event"]),
    ...mapGetters("event", ["eventId"]),

    ...mapState("eventCatalog", [
      "status",
      "step",
      "alphabetList",
      "currentCurrencyType"
    ]),
    ...mapGetters("eventCatalog", [
      "isNextStepAllowed",
      "totalOrderSum",
      "totalOrderSumWithVAT",
      "currencySymbol",
      "errorPriceServices",
      "priceTypes"
    ]),

    nextLabel() {
      return this.step < steps.PREVIEW
        ? this.$t("event.catalog.modal.footer.nextBtn")
        : this.$t("event.catalog.modal.footer.sendBtn");
    },

    isPreviewStep() {
      return this.step === steps.PREVIEW;
    },
    locale() {
      return i18n.locale;
    }
  },

  methods: {
    ...mapMutations("eventCatalog", ["UPDATE_STEP"]),
    ...mapMutations("event", ["UPDATE_CATALOG_STATUS"]),

    scrollToTop() {
      this.$emit("scroll-to-top");

      // MODAL
      // this.$parent.scrollToTop();
    },

    closeModal() {
      this.UPDATE_CATALOG_STATUS({
        field: "has_been_ordered",
        value: false
      });

      this.$modal.hide("event-modal-catalog");
    },

    next() {
      if (this.step < steps.PREVIEW) {
        this.$emit("next");
      } else {
        this.isSubmitAgree = true;
      }
    },

    prev() {
      this.UPDATE_STEP(this.step - 1);
      this.scrollToTop();
    },

    async submittionAgreed() {
      this.isSubmitAgree = false;

      const response = await apiCatalog.submit(
        this.eventId,
        this.event.catalog_order_id
      );
      if (response) {
        await this.$store.dispatch("eventCatalog/fetchData", {
          eventId: this.eventId,
          orderId: this.event.catalog_order_id
        });
        await this.$store.dispatch(
          "event/updateCatalogStatuses",
          response.data.status
        );
        this.$emit("closeModal");
      }
    },

    setCurrentCurrencyTypeHandler(type) {
      this.$store.dispatch("eventCatalog/setCurrentCurrencyType", type);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.event-modal-catalog-footer {
  position: sticky;
  bottom: 0;

  background: #fff;

  border-top: 2px solid #f1f1f1;

  padding: 25px 0;
  z-index: 50;
  @include respond-down(md) {
    flex-shrink: 0;
  }

  &__container {
    width: 780px;
    margin-left: 80px;

    @include respond-down(lg) {
      width: 100%;
      margin: 0;
      box-sizing: border-box;
      padding: 0 24px;
    }
  }

  &__text {
    font-size: 13px;
    line-height: 1.54;
    color: #6d7179;
    margin-bottom: 15px;

    span {
      color: #000000;
    }
  }

  &__info {
    font-size: 13px;
    line-height: 1.54;
    color: #000000;
    margin-bottom: 15px;

    span.primary {
      color: #6d7179;
    }
  }

  &__summary {
    display: flex;
    align-items: center;
    @include respond-down(md) {
      flex-wrap: wrap;
    }

    &-price {
      @include h3();

      margin-right: auto;

      span {
        font-size: 21px;
        font-weight: bold;

        margin-left: 5px;

        color: #005197;
      }

      @include respond-down(md) {
        width: 100%;
        margin-bottom: 20px;
      }

      &-wrapper {
        display: flex;
      }

      &-line {
        display: flex;
        align-items: center;
      }
    }

    &-back {
      margin-right: 70px;

      @include respond-down(md) {
        margin-right: auto;
      }
    }
  }

  &__error-price {
    display: inline-flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-content: flex-start;
    align-items: flex-start;
    max-width: 100%;
    margin-bottom: 40px;
    padding: 12px 20px;
    font-weight: 500;
    font-size: 13px;
    line-height: 20px;
    color: #f6931d;
    border-radius: 2px;
    background: #faebe8;

    @include respond-down(lg) {
      margin-bottom: 20px;
    }
    .svg-icon {
      display: inline-block;
      vertical-align: middle;
      min-width: 16px;
      width: 16px;
      height: 16px;
      margin-top: 2px;
      margin-right: 12px;
      fill: #f6931d;
    }
    &-text {
      flex: 1;
    }
  }
}
.popup-modal__agreed {
  a {
    @include hyperlink();
    font-size: 16px;
  }
}
</style>
